import { useEffect, useRef } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '../services/api-client';
import type {
  CreateSearchPayload,
  CreateSearchResponse,
  JobStatus,
  PageEnvelope,
  ResultDetail,
  ResultFilters,
  ResultItem,
  SearchDetail,
  SearchSummary,
} from '../types/api';

export const searchKeys = {
  all: ['searches'] as const,
  list: () => [...searchKeys.all, 'list'] as const,
  detail: (id: string) => [...searchKeys.all, 'detail', id] as const,
  // Prefixo sem os filtros: é por ele que se invalida a lista inteira, em
  // todas as combinações de filtro já visitadas, de uma vez só.
  resultsFor: (id: string) => [...searchKeys.all, 'results', id] as const,
  results: (id: string, filters: ResultFilters) => [...searchKeys.resultsFor(id), filters] as const,
  result: (id: string) => ['results', id] as const,
};

const TERMINAL_STATUSES: readonly JobStatus[] = ['COMPLETED', 'PARTIAL', 'FAILED', 'CANCELLED'];

export function isTerminalStatus(status: JobStatus | undefined): boolean {
  return status !== undefined && TERMINAL_STATUSES.includes(status);
}

export const POLL_INTERVAL_MS = 2000;

export function useSearchList(limit = 20) {
  return useInfiniteQuery({
    queryKey: searchKeys.list(),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      apiRequest<PageEnvelope<SearchSummary>>('/searches', {
        query: { cursor: pageParam ?? undefined, limit },
      }),
    getNextPageParam: (lastPage) => lastPage.page.nextCursor,
  });
}

/**
 * Detalhe da pesquisa com polling enquanto o job não termina (SPEC-014 §9).
 *
 * O polling para em status terminal: uma aba esquecida aberta não pode ficar
 * consultando a API pelo resto do dia.
 */
export function useSearchDetail(searchId: string | undefined) {
  return useQuery({
    queryKey: searchKeys.detail(searchId ?? ''),
    enabled: Boolean(searchId),
    queryFn: () => apiRequest<SearchDetail>(`/searches/${searchId}`),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return isTerminalStatus(status) ? false : POLL_INTERVAL_MS;
    },
  });
}

export function useCreateSearch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateSearchPayload) =>
      apiRequest<CreateSearchResponse>('/searches', { method: 'POST', body: payload }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: searchKeys.list() });
    },
  });
}

export function useCancelSearch(searchId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiRequest<{ status: JobStatus }>(`/searches/${searchId}/cancel`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: searchKeys.detail(searchId) });
      void queryClient.invalidateQueries({ queryKey: searchKeys.list() });
    },
  });
}

/**
 * Lista de resultados da pesquisa.
 *
 * O `status` não é enfeite: sem ele esta lista mente. Ela é montada junto com
 * o detalhe, e nesse instante o worker ainda está gravando — a primeira leitura
 * volta vazia, ou com as poucas linhas que já existiam. Como aqui não há
 * polling, `refetchOnWindowFocus` é `false` e nada mais toca nesta chave, essa
 * leitura vazia ficava congelada: o usuário via o status virar "Concluída", o
 * cabeçalho anunciar doze resultados e a lista continuar vazia até recarregar a
 * página ou mexer num filtro (que troca a `queryKey`).
 *
 * A invalidação acontece na **transição** para terminal, não a cada render com
 * status terminal — abrir uma pesquisa antiga faria uma segunda busca imediata,
 * idêntica à primeira, por nada.
 */
export function useResults(
  searchId: string | undefined,
  filters: ResultFilters,
  status: JobStatus | undefined,
  limit = 25,
) {
  const queryClient = useQueryClient();
  // `undefined` é "o detalhe ainda não chegou", não "está em andamento".
  // Confundir os dois faria toda pesquisa já concluída ser lida duas vezes ao
  // abrir: a primeira com status ausente, a segunda ao ele chegar terminal.
  const viuEmAndamento = useRef(false);

  useEffect(() => {
    if (status === undefined) return;

    if (!isTerminalStatus(status)) {
      viuEmAndamento.current = true;
      return;
    }

    if (viuEmAndamento.current && searchId) {
      viuEmAndamento.current = false;
      void queryClient.invalidateQueries({ queryKey: searchKeys.resultsFor(searchId) });
    }
  }, [queryClient, searchId, status]);

  return useInfiniteQuery({
    queryKey: searchKeys.results(searchId ?? '', filters),
    enabled: Boolean(searchId),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      apiRequest<PageEnvelope<ResultItem>>('/results', {
        query: {
          searchId,
          cursor: pageParam ?? undefined,
          limit,
          platform: filters.platform,
          author: filters.author,
          type: filters.type,
          keyword: filters.keyword,
          sort: filters.sort,
        },
      }),
    getNextPageParam: (lastPage) => lastPage.page.nextCursor,
  });
}

export function useResultDetail(resultId: string | undefined) {
  return useQuery({
    queryKey: searchKeys.result(resultId ?? ''),
    enabled: Boolean(resultId),
    queryFn: () => apiRequest<ResultDetail>(`/results/${resultId}`),
  });
}
